/*
 * Copyright (c) 2026 SimplyBook.me Ltd. All rights reserved.
 * Licensed under the Apache License, Version 2.0.
 */

import { useState, useEffect, useCallback } from "react";

const useStorage = (key, initialValue, storageType = "local") => {
  const storageObject = storageType === "session" ? window.sessionStorage : window.localStorage;

  const [storedValue, setStoredValue] = useState(() => {
    try {
      const item = storageObject.getItem(key);

      return item ? JSON.parse(item) : initialValue;
    } catch (error) {
      console.error(`Error reading from ${storageType}Storage:`, error);

      return initialValue;
    }
  });

  const setValue = useCallback(
    (value) => {
      try {
        const valueToStore = value instanceof Function ? value(storedValue) : value;
        setStoredValue(valueToStore);

        if (valueToStore === null) {
          storageObject.removeItem(key);
          setStoredValue(null);
        } else {
          storageObject.setItem(key, JSON.stringify(valueToStore));
        }
      } catch (error) {
        console.error("Storage operation failed:", error);
      }
    },
    [key, storedValue, storageObject]
  );

  useEffect(() => {
    const handleStorageChange = (event) => {
      if (event.key === key && event.storageArea === storageObject) {
        setStoredValue(JSON.parse(event.newValue));
      }
    };

    window.addEventListener("storage", handleStorageChange);

    return () => {
      window.removeEventListener("storage", handleStorageChange);
    };
  }, [key, storageObject]);

  return [storedValue, setValue];
};

export const useLocalStorage = (key, initialValue) => useStorage(key, initialValue, "local");
export const useSessionStorage = (key, initialValue) => useStorage(key, initialValue, "session");

export default useStorage;
